/**
 * @file address.ts
 * @description GCOME_AP 信息区（Zone）容量定义与多板载地址计算。
 *
 * 不同命令对应不同信息区，每个信息区有固定的容量（字节数）。
 * 多板载场景下，第 N 个板载的起始地址 = N × capacity。
 * 同一信息区的 get/set 命令共享容量和地址空间。
 *
 * 地址模式：
 * - 'profile'  — 按板载均分地址（标准模式，地址 = profile × capacity）
 * - 'continuous' — 连续地址模式（宏专用，不分板载，直接用连续地址）
 */

import { GCOME_AP_CMD } from './cmd'

/** 信息区地址模式。 */
export type ZoneAddressMode = 'profile' | 'continuous'

/**
 * 信息区定义。
 * - addressMode: 'profile' 按板载均分 | 'continuous' 连续地址
 * - capacity: 该信息区每板载占用的字节数（仅 profile 模式有意义）
 * - commands: 属于该信息区的命令列表（get/set 共享同一区）
 */
export interface GcomeApZoneEntry {
  readonly addressMode: ZoneAddressMode
  readonly capacity: number
  readonly commands: readonly number[]
}

export const GCOME_AP_ZONE: Record<string, GcomeApZoneEntry> = {
  // 基本信息区：32 字节
  info: { addressMode: 'profile', capacity: 32, commands: [GCOME_AP_CMD.getInfo] },
  // 板载控制区：32 字节
  base: { addressMode: 'profile', capacity: 32, commands: [GCOME_AP_CMD.getBase] },
  // 功能变量控制：64 字节 * N
  func: {
    addressMode: 'profile',
    capacity: 64,
    commands: [GCOME_AP_CMD.getFunc, GCOME_AP_CMD.setFunc],
  },
  // 自定义灯光定义区：512 字节 * N
  led: {
    addressMode: 'profile',
    capacity: 512,
    commands: [GCOME_AP_CMD.getLedDefine, GCOME_AP_CMD.setLedDefine],
  },
  // 按键矩阵定义区：512*4 字节 * N
  keyMatrix: {
    addressMode: 'profile',
    capacity: 512 * 4,
    commands: [
      GCOME_AP_CMD.getDefaultKeyMatrix,
      GCOME_AP_CMD.getUseKeyMatrix,
      GCOME_AP_CMD.setUseKeyMatrix,
    ],
  },
  // 宏按键信息定义：2K 空间，连续地址模式
  // 因为空间有限，32 个宏共享数据区，不是平均划分，由 AP 进行空间管理
  // 所以宏的 2k 空间不像其他指令按板载均分地址，直接用连续地址进行划分
  // 规则：指针储存区 + 数据区
  //   指针储存区：2 个字节为一个宏指针，指向该宏按键的数据地址；前 32*2=64 字节固定为宏指针空间
  //   数据区：指针储存区固定 64 个字节之后，额外空 4 个字节，开始存放宏按键数据，
  //           4 个字节为一个动作：2 字节延时 + 1 字节类型 + 1 字节 code，规则见文档
  macro: {
    addressMode: 'continuous',
    capacity: 1024 * 2,
    commands: [GCOME_AP_CMD.getMacro, GCOME_AP_CMD.setMacro],
  },
  // 按键触发参数：1024 字节 * N
  travel: {
    addressMode: 'profile',
    capacity: 1024,
    commands: [GCOME_AP_CMD.getKeyTriggerTravel, GCOME_AP_CMD.setKeyTriggerTravel],
  },
  // DKS 动态行程信息区：768 * N
  dks: {
    addressMode: 'profile',
    capacity: 768,
    commands: [GCOME_AP_CMD.getDksInfo, GCOME_AP_CMD.setDksInfo],
  },
  // MT 按键信息区：256 字节 * N
  // RS/SOCD/OKS 按键信息区：公用 MT 按键信息区
  mt: {
    addressMode: 'profile',
    capacity: 256,
    commands: [GCOME_AP_CMD.getMtKeyInfo, GCOME_AP_CMD.setMtKeyInfo],
  },
  // TGL 按键信息区：128 字节 * N
  tgl: {
    addressMode: 'profile',
    capacity: 128,
    commands: [GCOME_AP_CMD.getTglKeyInfo, GCOME_AP_CMD.setTglKeyInfo],
  },
  // APP 自行管理的数据：1024 字节
  app: {
    addressMode: 'profile',
    capacity: 1024,
    commands: [GCOME_AP_CMD.getAppDefine, GCOME_AP_CMD.setAppDefine],
  },
  // 按键校准信息区：512 字节
  calibration: {
    addressMode: 'profile',
    capacity: 512,
    commands: [
      GCOME_AP_CMD.getCalibrationInfo,
      GCOME_AP_CMD.startCalibration,
      GCOME_AP_CMD.endCalibration,
    ],
  },
} as const

/** 从信息区自动生成的 "命令 → 容量" 查找表。 */
export const GCOME_AP_CMD_CAPACITY: Record<number, number> = {}
for (const zone of Object.values(GCOME_AP_ZONE)) {
  for (const cmd of zone.commands) {
    GCOME_AP_CMD_CAPACITY[cmd] = zone.capacity
  }
}

/** 查找命令所属的信息区。 */
function findZoneByCommand(command: number): GcomeApZoneEntry {
  for (const zone of Object.values(GCOME_AP_ZONE)) {
    if (zone.commands.includes(command)) {
      return zone
    }
  }
  throw new Error(
    `命令 0x${command.toString(16).padStart(2, '0').toUpperCase()} 未找到对应信息区。`,
  )
}

/**
 * 计算多板载场景下的起始地址。
 * - 'profile' 模式：地址 = profile × capacity
 * - 'continuous' 模式：始终返回 0（宏等连续地址区不分板载）
 * @param command  命令码
 * @param profile  板载序号（0-based），默认为 0
 * @returns 该板载对应信息区的起始地址
 */
export function calculateGcomeApAddress(command: number, profile = 0): number {
  const zone = findZoneByCommand(command)
  if (zone.addressMode === 'continuous') {
    return 0
  }
  return profile * zone.capacity
}

// ─── 宏（Macro）专用地址函数 ────────────────────────────────────────────────

/** 宏指针区的固定大小：32 个宏 × 2 字节。 */
const MACRO_POINTER_AREA_SIZE = 32 * 2

/** 指针区之后固定的空 4 字节，这里是抓包发现的规则，实际并非文档有强制规定。 */
const MACRO_RESERVED_SIZE = 4

/** 宏数据区的起始偏移。 */
const MACRO_DATA_BASE = MACRO_POINTER_AREA_SIZE + MACRO_RESERVED_SIZE

/** 宏最大索引。 */
const MACRO_MAX_INDEX = 31

/**
 * 计算宏按键指针在指针区的地址。
 * @param macroIndex  宏索引（0-31）
 * @returns 指针在宏信息区中的字节偏移
 */
export function calculateMacroPointerAddress(macroIndex: number): number {
  if (!Number.isInteger(macroIndex) || macroIndex < 0 || macroIndex > MACRO_MAX_INDEX) {
    throw new Error(`宏索引必须为 0-31，当前为 ${macroIndex}。`)
  }
  return macroIndex * 2
}

/**
 * 计算宏数据区的起始地址（固定为 68，指针区 64 字节 + 保留 4 字节之后）。
 * 宏指针值为数据区的绝对地址（指针 0 表示宏未定义），首个宏的数据位置即 68。
 */
export function calculateMacroDataBaseAddress(): number {
  return MACRO_DATA_BASE
}
